import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Divider,
  Breadcrumbs,
  Link,
  LinearProgress,
  Tooltip,
  Paper,
  Stack,
  Avatar
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  CompareArrows as CompareArrowsIcon,
  Category as CategoryIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Support as SupportIcon,
  Home as HomeIcon,
  Insights as InsightsIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import Layout from './Layout';

const Administracao = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [dePara, setDePara] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [tiposAtendimento, setTiposAtendimento] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('Token não encontrado, redirecionando para login...');
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fazendo todas as chamadas em paralelo
        const [
          usuariosResponse,
          empresasResponse,
          deParaResponse,
          categoriasResponse,
          responsaveisResponse,
          tiposAtendimentoResponse,
          historicoResponse
        ] = await Promise.all([
          api.get('/usuarios'),
          api.get('/empresas'),
          api.get('/depara'),
          api.get('/categorias'),
          api.get('/responsaveis'),
          api.get('/tipos-atendimento'),
          api.get('/historico-responsaveis'),
        ]);

        setUsuarios(usuariosResponse.data.usuarios || []);
        setEmpresas(empresasResponse.data.empresas || []);
        setDePara(deParaResponse.data.depara || []);
        setCategorias(categoriasResponse.data.categorias || []);
        setResponsaveis(responsaveisResponse.data.responsaveis || []);
        setTiposAtendimento(tiposAtendimentoResponse.data.tipos_atendimento || []);
        setHistorico(historicoResponse.data.historico || []);
      } catch (error) {
        console.error('Erro ao carregar dados administrativos:', error);
        setError('Erro ao carregar dados. Por favor, tente novamente.');
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const adminCards = [
    {
      title: 'Usuários',
      count: usuarios.length,
      icon: <PeopleIcon fontSize="large" color="primary" />,
      route: '/admin/usuarios',
      description: 'Gerenciar os usuários do sistema',
      category: 'Permissões'
    },
    {
      title: 'Empresas',
      count: empresas.length,
      icon: <BusinessIcon fontSize="large" color="primary" />,
      route: '/admin/empresas',
      description: 'Gerenciar cadastro de empresas',
      category: 'Entidades'
    },
    {
      title: 'De/Para',
      count: dePara.length,
      icon: <CompareArrowsIcon fontSize="large" color="primary" />,
      route: '/admin/depara',
      description: 'Mapeamentos Empresa-Seguradora',
      category: 'Entidades'
    },
    {
      title: 'Seguradoras',
      count: '✓',
      icon: <InsightsIcon fontSize="large" color="secondary" />,
      route: '/admin/seguradoras',
      description: 'Gerenciar seguradoras do sistema',
      category: 'Entidades'
    },
    {
      title: 'Mapeamentos',
      count: '✓',
      icon: <CompareArrowsIcon fontSize="large" color="success" />,
      route: '/admin/empresa-seguradora',
      description: 'Gerenciar mapeamentos empresa-seguradora',
      category: 'Entidades'
    },
    {
      title: 'Categorias',
      count: categorias.length,
      icon: <CategoryIcon fontSize="large" color="primary" />,
      route: '/admin/categorias',
      description: 'Gerenciar categorias',
      category: 'Classificação'
    },
    {
      title: 'Responsáveis',
      count: responsaveis.length,
      icon: <SupervisorAccountIcon fontSize="large" color="primary" />,
      route: '/admin/responsaveis',
      description: 'Gerenciar responsáveis',
      category: 'Permissões'
    },
    {
      title: 'Responsável / Empresa',
      count: historico.length,
      icon: <HistoryIcon fontSize="large" color="primary" />,
      route: '/admin/historico-responsaveis',
      description: 'Gerenciar histórico de responsáveis por empresa',
      category: 'Permissões'
    },
    {
      title: 'Tipos de Atendimento',
      count: tiposAtendimento.length,
      icon: <SupportIcon fontSize="large" color="primary" />,
      route: '/admin/tipos-atendimento',
      description: 'Gerenciar tipos de atendimento',
      category: 'Classificação'
    },
  ];

  // Agrupar os cards por categoria
  const groupedCards = adminCards.reduce((acc, card) => {
    if (!acc[card.category]) {
      acc[card.category] = [];
    }
    acc[card.category].push(card);
    return acc;
  }, {});

  return (
    <Layout>
      <Container maxWidth="lg">
        {/* Cabeçalho e navegação */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              href="/dashboard"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Início
            </Link>
            <Typography color="text.primary">Administração</Typography>
          </Breadcrumbs>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <InsightsIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  Painel Administrativo
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Gerencie todos os aspectos do sistema
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ width: '100%', mb: 4 }}>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
              Carregando dados administrativos...
            </Typography>
            <LinearProgress />
          </Box>
        ) : (
          <>
            {/* Mostrar cartões agrupados por categoria */}
            {Object.entries(groupedCards).map(([category, cards]) => (
              <Box key={category} sx={{ mb: 5 }}>
                <Paper 
                  elevation={0} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px'
                  }}
                >
                  <Typography variant="h6" color="primary" gutterBottom>
                    {category}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    {cards.map((card) => (
                      <Grid item xs={12} sm={6} md={4} key={card.title}>
                        <Tooltip title={`Ir para ${card.title}`} arrow>
                          <Card
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                              },
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                            onClick={() => navigate(card.route)}
                          >
                            <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                              <Box sx={{ 
                                mb: 2, 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                margin: '0 auto 16px'
                              }}>
                                {card.icon}
                              </Box>
                              <Typography variant="h5" component="h2" gutterBottom>
                                {card.title}
                              </Typography>
                              <Typography
                                variant="h3"
                                color="primary"
                                sx={{ mb: 2, fontWeight: 'bold' }}
                              >
                                {loading ? '...' : card.count}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {card.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            ))}
            
            {/* Visão geral do sistema */}
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: '8px',
                bgcolor: 'primary.main',
                color: 'white'
              }}
            >
              <Typography variant="h5" gutterBottom>
                Visão Geral do Sistema
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                Seu sistema de administração conta com:
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{usuarios.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Usuários</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{empresas.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Empresas</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{dePara.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>De/Para</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{categorias.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Categorias</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{responsaveis.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Responsáveis</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4">{tiposAtendimento.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Tipos de Atendimento</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Container>
    </Layout>
  );
};

export default Administracao; 